import { useState, type ReactNode } from 'react';
import { Lock } from 'lucide-react';
import { ZONE_SSL_MODES } from '@cp/shared';
import { Card, ConfirmDialog, EmptyState, Fact, Facts, Select, Switch } from '@/components/ui';
import type { ZoneSectionProps } from '@/components/sites/dns/ZoneSections';
import { useSetAlwaysHttps, useSetSslMode } from '@/hooks/useDomains';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { ZONE_SSL_LABELS } from '@/utils/labels';

/** What each mode does, said where the mode is chosen: a wrong one takes a site down. */
const SSL_HINTS: Record<string, string> = {
  off: 'Без HTTPS. Посетители увидят сайт по HTTP.',
  flexible: 'HTTPS до Cloudflare, HTTP до сервера. Если сервер перенаправляет на HTTPS, возникает цикл.',
  full: 'HTTPS до сервера без проверки сертификата. Для самоподписанного сертификата.',
  strict: 'HTTPS до сервера с проверкой сертификата. Без действительного сертификата — ошибка 526.',
};

const SSL_OPTIONS = ZONE_SSL_MODES.map((mode) => ({ value: mode, label: ZONE_SSL_LABELS[mode] ?? mode }));

/** The domain's Cloudflare SSL mode and Always Use HTTPS: both for the whole domain. */
export function SslSection({ siteId, zone, may }: ZoneSectionProps): ReactNode {
  const [sslTo, setSslTo] = useState<string | null>(null);
  const domainId = zone.domainId ?? '';
  const ssl = useSetSslMode(siteId, domainId, {
    onSuccess: (result) => {
      toast.success('Режим SSL изменён', ZONE_SSL_LABELS[result.sslMode] ?? result.sslMode);
      setSslTo(null);
    },
  });
  const https = useSetAlwaysHttps(siteId, domainId, {
    onSuccess: (result) => toast.success(result.alwaysUseHttps ? 'Always Use HTTPS включён' : 'Always Use HTTPS выключен'),
    onError: (error) => toast.error('Не удалось изменить', errorText(error)),
  });

  if (!zone.cloudflare) {
    return (
      <Card flush title="SSL и HTTPS" icon={Lock}>
        <EmptyState icon={Lock} message="Домен не обслуживается Cloudflare" />
      </Card>
    );
  }

  return (
    <Card title="SSL и HTTPS" icon={Lock}>
      <Facts>
        <Fact label="Режим SSL">
          <div className="stack stack-tight" style={{ maxWidth: 420 }}>
            <Select
              label="Режим SSL"
              size="sm"
              value={zone.sslMode ?? ''}
              onChange={(mode) => mode !== zone.sslMode && setSslTo(mode)}
              options={SSL_OPTIONS}
              disabled={!may}
              placeholder="неизвестно"
            />
            {zone.sslMode ? <span className="field-hint">{SSL_HINTS[zone.sslMode]}</span> : null}
          </div>
        </Fact>
        <Fact label="Always Use HTTPS">
          <div className="stack stack-tight">
            <Switch label="Always Use HTTPS" checked={zone.alwaysUseHttps === true} disabled={!may || https.isPending} onChange={(enabled) => https.mutate(enabled)} />
            <span className="field-hint">Cloudflare перенаправляет http:// на https://.</span>
          </div>
        </Fact>
      </Facts>

      <ConfirmDialog
        open={sslTo !== null}
        title="Сменить режим SSL"
        description={sslTo ? `${ZONE_SSL_LABELS[zone.sslMode ?? ''] ?? '—'} → ${ZONE_SSL_LABELS[sslTo] ?? sslTo}` : undefined}
        confirmLabel="Сменить"
        busy={ssl.isPending}
        error={ssl.isError ? errorText(ssl.error) : undefined}
        onConfirm={() => sslTo && ssl.mutate(sslTo)}
        onClose={() => {
          ssl.reset();
          setSslTo(null);
        }}
      >
        <p className="field-hint">
          {sslTo ? SSL_HINTS[sslTo] : null} Режим действует для всего домена {zone.apex}.
        </p>
      </ConfirmDialog>
    </Card>
  );
}
